import React from 'react';
import styled from 'styled-components';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { DocumentPageListItem } from '../../types';
import { Badge } from '../shared/Badge';
import { highlightMatches } from '../../utils/highlight';

export type DocumentPageRailInteractionEvent =
    React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>;

/** Where a drop lands relative to the target page. */
export type RailDropPosition = 'before' | 'after';

export interface SelectionPillConfig {
    /** Formatted ranges string, e.g. "1-3, 5". */
    value: string;
    /** True when the last commit produced no valid selection — pill renders in error state. */
    invalid?: boolean;
    /** Commit handler: called with the raw text the user typed. */
    onCommit: (text: string) => void;
    /** Clear the entire selection (X on the pill). */
    onClear: () => void;
}

export interface DocumentPageRailProps {
    /** Search string used to highlight matches inside page snippets. The input
     *  itself lives in the toolbar; this just receives the current value. */
    searchValue?: string;
    pages: DocumentPageListItem[];
    selectedCount?: number;
    /** Current rail width in pixels; drives thumbnail scaling (1x at min width, up to 1.5x at max). */
    railWidth?: number;
    railMinWidth?: number;
    railMaxWidth?: number;
    /** Collapsed state and its toggle. When true, the rail shows only an
     *  expand handle and hides all page content. */
    collapsed?: boolean;
    onToggleCollapsed?: () => void;
    /** Thumbnail / label click — navigates the canvas viewer to that page. Does NOT toggle selection. */
    onPageClick?: (pageId: string, event: DocumentPageRailInteractionEvent) => void;
    /** Checkbox toggle on a page item. `extend=true` for shift-click (range select from the last toggled anchor). */
    onToggleSelect?: (pageId: string, extend: boolean) => void;
    /** Drag-and-drop page reorder. Called when a dragged page is dropped onto a
     *  target page. `draggedPageId` is the page the user grabbed; if it is part
     *  of the current selection the whole selection moves as one contiguous
     *  chunk, otherwise just that page moves. `position` says whether the drop
     *  lands before or after `targetPageId`. Omit to disable rail reordering. */
    onReorderDrop?: (
        draggedPageId: string,
        targetPageId: string,
        position: RailDropPosition,
    ) => void;
    /** When this value changes, the page list scrolls back to the top. */
    scrollResetKey?: string | number | null;
    /** Page the main viewer is currently on — the rail keeps its thumbnail in
     *  view as the user scrolls the full-page display. */
    activePageId?: string | null;
}

const BASE_THUMB_W = 44;
const BASE_THUMB_H = 58;
const MAX_THUMB_SCALE = 1.5;

const Panel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    height: 100%;
    flex: 1 1 auto;
    min-height: 0;
    overflow: visible;
`;

const TopBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
`;

const CollapseButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 4px;
    border: none;
    background: transparent;
    color: #999;
    cursor: pointer;

    &:hover {
        background: #eee;
        color: #555;
    }
`;

const CollapsedPanel = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 8px;
    height: 100%;
`;

const PageList = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
`;

const PageItem = styled.div<{ $selected?: boolean; $highlighted?: boolean; $dragging?: boolean }>`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px;
    border-radius: 6px;
    border: 1.5px solid ${({ $selected, $highlighted }) =>
        $selected ? '#111' : $highlighted ? '#bbb' : 'transparent'};
    background: ${({ $selected }) => ($selected ? '#fafafa' : '#f8f8f8')};
    text-align: center;
    cursor: pointer;
    opacity: ${({ $dragging }) => ($dragging ? 0.4 : 1)};
    transition: border-color 0.1s, background 0.1s, opacity 0.1s;

    &:hover {
        background: ${({ $selected }) => ($selected ? '#fafafa' : '#f2f2f2')};
    }
`;

/* A page row + its drop-indicator slot. The inter-row spacing lives here as
   padding (NOT as a PageList gap) so every pixel of the rail — including the
   visual gap between rows — belongs to exactly one slot. That makes the drag
   hover target and the drop hit target identical: without this, the pointer can
   sit in a dead gap that highlights a boundary (nearest midpoint) but is over
   no droppable element, so the drop silently misses. The 2px indicator lines
   are drawn INSIDE the padding band via inset pseudo-elements. */
const PageSlot = styled.div<{ $dropBefore?: boolean; $dropAfter?: boolean }>`
    position: relative;
    padding: 2px 0;

    &::before,
    &::after {
        content: '';
        position: absolute;
        left: 2px;
        right: 2px;
        height: 2px;
        border-radius: 2px;
        background: #2563eb;
        pointer-events: none;
    }
    &::before {
        top: 0;
        display: ${({ $dropBefore }) => ($dropBefore ? 'block' : 'none')};
    }
    &::after {
        bottom: 0;
        display: ${({ $dropAfter }) => ($dropAfter ? 'block' : 'none')};
    }
`;

/** Custom MIME type on the drag payload so the rail only accepts its own drags
 *  (a file/text drag from elsewhere won't register as a page reorder). */
const RAIL_DRAG_MIME = 'application/x-bigpdf-rail-page';

const PageItemBody = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    flex: 1 1 auto;
    min-width: 0;
`;

const ThumbnailRow = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: stretch;
`;

/* Badges overlay the thumbnail's top-right corner — the rail is too narrow
   for a side column. Tooltips carry the same meaning as before. */
const BadgeColumn = styled.div`
    position: absolute;
    top: 2px;
    right: 2px;
    display: flex;
    flex-direction: column;
    gap: 3px;
    align-items: flex-end;
    pointer-events: none;

    & > * {
        pointer-events: auto;
    }
`;

/* Selection checkbox overlays the thumbnail's top-left corner; hidden until
   the row is hovered/focused or the page is selected (compact-rail rule). */
const PageCheckbox = styled.input.attrs({ type: 'checkbox' })`
    position: absolute;
    top: 2px;
    left: 2px;
    margin: 0;
    cursor: pointer;
    z-index: 1;
    opacity: 0;
    transition: opacity 0.1s;

    &:checked,
    &:focus-visible {
        opacity: 1;
    }

    ${PageItem}:hover & {
        opacity: 1;
    }
`;

const ThumbnailWrapper = styled.div<{ $width: number; $height: number }>`
    position: relative;
    width: ${({ $width }) => $width}px;
    height: ${({ $height }) => $height}px;
    border-radius: 3px;
    overflow: hidden;
    background: linear-gradient(145deg, #e8e4df 0%, #ddd8d0 100%);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    flex-shrink: 0;
`;

const ThumbnailImage = styled.img`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const ThumbnailSkeleton = styled.div`
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, rgba(226, 232, 240, 0.6) 0%, rgba(248, 250, 252, 0.9) 50%, rgba(226, 232, 240, 0.6) 100%);
    animation: pulse 1.2s ease-in-out infinite;

    @keyframes pulse {
        0% {
            opacity: 0.7;
        }
        50% {
            opacity: 1;
        }
        100% {
            opacity: 0.7;
        }
    }
`;

/* Compact page number: a small chip pinned to the thumbnail's bottom edge
   (replaces the old "p. N" caption block below the thumb). */
const PageNumberChip = styled.div`
    position: absolute;
    bottom: 2px;
    left: 50%;
    transform: translateX(-50%);
    padding: 0 5px;
    border-radius: 3px;
    background: rgba(255, 255, 255, 0.88);
    font-size: 10px;
    font-weight: 600;
    color: #555;
    line-height: 1.5;
    pointer-events: none;
`;

const EmptyHint = styled.div`
    padding: 12px 10px;
    font-size: 12px;
    color: #888;
    text-align: center;
`;

const PageSnippet = styled.div`
    font-size: 11px;
    color: #777;
    line-height: 1.3;
    font-style: italic;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-align: left;
    width: 100%;

    mark {
        background: #fde68a;
        color: inherit;
        padding: 0 1px;
        border-radius: 2px;
    }
`;

/** Render the per-page badge overlay (uncertain boundary only).
 *  Returns null when there's nothing to show so the overlay collapses.
 *  Duplicate-page badges are intentionally not shown (detection still runs and
 *  drives the duplicates view; the per-page badge is just hidden). */
const renderRailBadges = (page: DocumentPageListItem): React.ReactNode => {
    const conf = page.boundaryAfter?.confidence;
    const showBoundary = conf != null && conf >= 0.4 && conf <= 0.6;
    if (!showBoundary) {
        return null;
    }
    return (
        <BadgeColumn>
            {showBoundary && (
                <Badge type="quality" title="Possible document boundary on this page" />
            )}
        </BadgeColumn>
    );
};

export const DocumentPageRail: React.FC<DocumentPageRailProps> = ({
    searchValue = '',
    pages,
    railWidth,
    railMinWidth = 140,
    railMaxWidth = 480,
    collapsed = false,
    onToggleCollapsed,
    onPageClick,
    onToggleSelect,
    onReorderDrop,
    scrollResetKey,
    activePageId,
}) => {
    // Drag-and-drop reorder. Design constraints that the earlier attempts got
    // wrong, per the HTML5 DnD contract:
    //
    //  1. `drop` fires on an element ONLY if EVERY `dragover` over it called
    //     preventDefault(). So dragover must call preventDefault unconditionally
    //     for a rail drag — never gate it on React state, or the region silently
    //     becomes non-droppable and the drop is swallowed.
    //  2. `dataTransfer.getData()` returns "" during dragover (data is only
    //     readable on drop). So the *identity* of the dragged page can't come
    //     from dataTransfer mid-drag — we keep it in a ref (synchronous, no
    //     stale-closure/async-state window) set on dragStart.
    //  3. React state (`dropTarget`) is used ONLY to render the indicator, never
    //     to authorize or position the drop. The drop reads geometry from its
    //     own event against the DOM row, so a stale hover can't misdirect it.
    //
    // Handlers are bound once at the PageList level and resolve the row under the
    // pointer from the DOM (`data-rail-page-id`), so there are no per-row
    // closures capturing a moving `draggingId`.
    const dragEnabled = !!onReorderDrop;
    const draggingIdRef = React.useRef<string | null>(null);
    const [draggingId, setDraggingId] = React.useState<string | null>(null);
    const [dropTarget, setDropTarget] = React.useState<{
        pageId: string;
        position: RailDropPosition;
    } | null>(null);

    // The set of pages that travel together: the whole selection if the grabbed
    // page is selected, else just the grabbed page.
    const movingChunkFor = React.useCallback(
        (grabbedId: string | null): Set<string> => {
            if (!grabbedId) return new Set();
            const selected = new Set(pages.filter(p => p.selected).map(p => p.id));
            return selected.has(grabbedId) ? selected : new Set([grabbedId]);
        },
        [pages],
    );

    // Resolve the rail page row under a pointer event, plus which half of it the
    // pointer is on. Returns null when the pointer isn't over a page row.
    const resolveRow = React.useCallback(
        (event: React.DragEvent<HTMLElement>): { pageId: string; position: RailDropPosition } | null => {
            const target = event.target as HTMLElement | null;
            const row = target?.closest<HTMLElement>('[data-rail-page-id]');
            const pageId = row?.dataset.railPageId;
            if (!row || !pageId) return null;
            const rect = row.getBoundingClientRect();
            const position: RailDropPosition =
                event.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
            return { pageId, position };
        },
        [],
    );

    const handleDragStart = React.useCallback(
        (pageId: string) => (event: React.DragEvent<HTMLDivElement>) => {
            if (!dragEnabled) return;
            event.dataTransfer.setData(RAIL_DRAG_MIME, pageId);
            event.dataTransfer.effectAllowed = 'move';
            draggingIdRef.current = pageId;   // synchronous — readable in dragover
            setDraggingId(pageId);            // for dimming the moving chunk
        },
        [dragEnabled],
    );

    const handleListDragOver = React.useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            const grabbed = draggingIdRef.current;
            if (!dragEnabled || grabbed == null) return;
            // Unconditionally allow the drop for a rail drag so `drop` will fire
            // anywhere over the list. (We still resolve WHERE, below, but never
            // withhold preventDefault — that is what made the target feel narrow.)
            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';

            const row = resolveRow(event);
            const chunk = movingChunkFor(grabbed);
            // No indicator when hovering a member of the moving chunk (nowhere to
            // land) — but the drop stays authorized so releasing there is a
            // harmless no-op rather than a swallowed event.
            if (!row || chunk.has(row.pageId)) {
                setDropTarget(prev => (prev === null ? prev : null));
                return;
            }
            setDropTarget(prev =>
                prev && prev.pageId === row.pageId && prev.position === row.position
                    ? prev
                    : row,
            );
        },
        [dragEnabled, resolveRow, movingChunkFor],
    );

    const clearDrag = React.useCallback(() => {
        draggingIdRef.current = null;
        setDraggingId(null);
        setDropTarget(null);
    }, []);

    const handleListDrop = React.useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            if (!dragEnabled) return;
            event.preventDefault();
            const dragged = draggingIdRef.current;
            const row = resolveRow(event);
            clearDrag();
            if (!dragged || !row) return;
            const chunk = movingChunkFor(dragged);
            if (chunk.has(row.pageId)) return;   // released onto the moving chunk
            onReorderDrop?.(dragged, row.pageId, row.position);
        },
        [dragEnabled, resolveRow, movingChunkFor, clearDrag, onReorderDrop],
    );
    // Linear interpolation from 1x at min rail width to MAX_THUMB_SCALE at max.
    const thumbScale = React.useMemo(() => {
        if (railWidth == null || railMaxWidth <= railMinWidth) {
            return 1;
        }
        const t = Math.max(0, Math.min(1, (railWidth - railMinWidth) / (railMaxWidth - railMinWidth)));
        return 1 + (MAX_THUMB_SCALE - 1) * t;
    }, [railWidth, railMinWidth, railMaxWidth]);
    const thumbW = Math.round(BASE_THUMB_W * thumbScale);
    const thumbH = Math.round(BASE_THUMB_H * thumbScale);

    // Reset the page list's scroll position whenever the parent indicates a
    // new context (typically a document switch).
    const pageListRef = React.useRef<HTMLDivElement | null>(null);
    React.useEffect(() => {
        if (pageListRef.current) {
            pageListRef.current.scrollTop = 0;
        }
    }, [scrollResetKey]);

    // Track the page the main viewer is on: keep its thumbnail visible.
    // `nearest` is a no-op when the thumb is already in view, so this doesn't
    // fight the user's own rail scrolling.
    React.useEffect(() => {
        if (!activePageId || !pageListRef.current) {
            return;
        }
        const thumb = pageListRef.current.querySelector(
            `[data-rail-page-id="${CSS.escape(activePageId)}"]`,
        );
        thumb?.scrollIntoView({ block: 'nearest' });
    }, [activePageId]);

    if (collapsed) {
        return (
            <CollapsedPanel>
                <CollapseButton
                    type="button"
                    aria-label="Expand page rail"
                    title="Expand page rail"
                    onClick={onToggleCollapsed}
                >
                    <ChevronRight size={14} />
                </CollapseButton>
            </CollapsedPanel>
        );
    }

    return (
        <Panel>
            <TopBar>
                <CollapseButton
                    type="button"
                    aria-label="Collapse page rail"
                    title="Collapse page rail"
                    onClick={onToggleCollapsed}
                >
                    <ChevronLeft size={14} />
                </CollapseButton>
            </TopBar>
            <PageList
                ref={pageListRef}
                onDragOver={dragEnabled ? handleListDragOver : undefined}
                onDrop={dragEnabled ? handleListDrop : undefined}
            >
                {/* Empty state — shown when a search is active but yields no current-doc pages. */}
                {pages.length === 0 && (searchValue ?? '').trim().length > 0 && (
                    <EmptyHint>No pages match “{searchValue}”.</EmptyHint>
                )}

                {/* Current document pages */}
                {pages.map(page => (
                    <PageSlot
                        key={page.id}
                        data-rail-page-id={page.id}
                        $dropBefore={dropTarget?.pageId === page.id && dropTarget.position === 'before'}
                        $dropAfter={dropTarget?.pageId === page.id && dropTarget.position === 'after'}
                    >
                        <PageItem
                            role="button"
                            tabIndex={0}
                            draggable={dragEnabled}
                            onDragStart={dragEnabled ? handleDragStart(page.id) : undefined}
                            onDragEnd={dragEnabled ? clearDrag : undefined}
                            onClick={event => onPageClick?.(page.id, event)}
                            onKeyDown={event => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    onPageClick?.(page.id, event);
                                }
                            }}
                            $selected={page.selected}
                            $highlighted={page.highlighted}
                            $dragging={draggingId != null && movingChunkFor(draggingId).has(page.id)}
                        >
                            <PageItemBody>
                                <ThumbnailRow>
                                    <ThumbnailWrapper $width={thumbW} $height={thumbH}>
                                        <PageCheckbox
                                            checked={!!page.selected}
                                            aria-label={`Select page ${page.pageNumber}`}
                                            onClick={event => {
                                                // Stop the outer PageItem onClick so the toggle
                                                // doesn't also navigate the canvas viewer.
                                                event.stopPropagation();
                                            }}
                                            onChange={event => {
                                                const native = event.nativeEvent as MouseEvent;
                                                onToggleSelect?.(page.id, !!native.shiftKey);
                                            }}
                                        />
                                        {page.thumbnailUrl ? (
                                            <ThumbnailImage
                                                src={page.thumbnailUrl}
                                                alt={`Page ${page.pageNumber} thumbnail`}
                                                loading="lazy"
                                                // The row (PageItem) owns the drag; a natively
                                                // draggable <img> would otherwise start its own
                                                // image drag when grabbed and hijack the reorder.
                                                draggable={false}
                                            />
                                        ) : (
                                            <ThumbnailSkeleton />
                                        )}
                                        <PageNumberChip aria-hidden>{page.pageNumber}</PageNumberChip>
                                        {renderRailBadges(page)}
                                    </ThumbnailWrapper>
                                </ThumbnailRow>
                                {page.ocrPreview && (
                                    <PageSnippet
                                        dangerouslySetInnerHTML={{
                                            __html: highlightMatches(page.ocrPreview, searchValue),
                                        }}
                                    />
                                )}
                            </PageItemBody>
                        </PageItem>
                    </PageSlot>
                ))}
            </PageList>
        </Panel>
    );
};
